import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { CONTACT } from "@/lib/data";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/founder", label: "Founder" },
  { to: "/services", label: "Services" },
  { to: "/products", label: "Products" },
  { to: "/deals", label: "Deals" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-[#0A0A0A]/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div
        className={`max-w-[1400px] mx-auto px-5 md:px-10 flex items-center justify-between gap-8 lg:gap-10 transition-[height] duration-500 ${
          scrolled ? "h-14 md:h-16" : "h-20 md:h-24"
        }`}
      >
        <Link to="/" className="flex items-center group shrink-0 py-2" data-testid="logo-link" aria-label="Saver Plus Lighting Corp — home">
          <img
            src="/assets/saver-plus-logo.png"
            alt="Saver Plus Lighting Corp logo"
            className={`w-auto transition-[height] duration-500 ${scrolled ? "h-8 md:h-9 lg:h-10" : "h-11 md:h-12 lg:h-16"}`}
            width="1774"
            height="887"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-sweep text-sm uppercase tracking-[0.12em] whitespace-nowrap transition-colors duration-300 ${
                  isActive ? "text-white" : "text-white/55 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/contact"
            data-testid="header-quote-cta"
            className="hidden sm:inline-flex items-center gap-2 bg-gold text-white text-xs uppercase tracking-[0.14em] font-semibold px-5 py-3 whitespace-nowrap shrink-0 hover:bg-[#b00000] transition-colors duration-300"
          >
            Request Quote
          </Link>
          <button
            className="lg:hidden grid place-items-center w-10 h-10 border border-white/15"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
            className="lg:hidden overflow-hidden bg-[#0A0A0A] border-b border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className="font-display uppercase text-3xl py-2 text-white/80"
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <a href={CONTACT.phoneHref} className="mt-4 text-gold uppercase tracking-widest text-sm">
                {CONTACT.phone}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
